﻿/*
 * Purpose: Display password strength and rule conformance as compact chips.
 * Boundary: Presentational only; derives counts locally from password props.
 * Invariants:
 * - Receives password and confirmPassword; never mutates external state.
 * - ASCII only; comments and strings are plain text.
 */

// Imports
import { memo } from "react";

// Component RuleChip:
// Presentational chip showing label and count with color-coded status
export function RuleChip({ ok, label, count }) {
    const bg = ok ? "bg-emerald-900/20" : "bg-orange-900/20";
    const border = ok ? "border-emerald-700/40" : "border-orange-700/40";
    const countbg = ok ? "bg-emerald-500/20" : "bg-orange-500/20";
    const countborder = ok ? "border-emerald-700/40" : "border-orange-700/40";
    const labelColor = `text-${ok ? "emerald" : "orange"}-300`;
    const countColor = `text-${ok ? "emerald" : "orange"}-200`;

    // Rendering - two-part chip: label on left, count on right
    return (
        <span className="inline-flex rounded-md">
            <span
                className={`flex items-center rounded-l-md pl-2 pr-1 md:pl-3 md:pr-2 py-0.5 md:py-1 border border-r-0 text-[11px] md:text-[13px] ${bg} ${border} ${labelColor}`}
            >
                {label}
                {ok ? " ✓" : ""}
            </span>
            <span
                className={`flex items-center rounded-r-md pl-1 pr-2 md:pl-2 md:pr-3 py-0.5 md:py-1 border border-l-0 text-[11px] md:text-[13px] ${countbg} ${countborder} ${countColor}`}
            >
                {count}
            </span>
        </span>
    );
}

// Component PasswordRulesChips:
// Stateless summary of password requirements via rule chips
// Receives password, confirmPassword, and optional OTP validation flag
const PasswordRulesChips = memo(
    ({ password = "", confirmPassword = "", type = "create", isOtpValid = true }) => {
        // Derived Values - calculate character counts and matching state
        const passwordLength = password.length;
        const uppercaseCount = (password.match(/[A-Z]/g) || []).length;
        const lowercaseCount = (password.match(/[a-z]/g) || []).length;
        const numberCount = (password.match(/\d/g) || []).length;
        const specialCount =
            (password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || []).length;
        const matches = password === confirmPassword && password !== "";

        // Rule Definitions - length, character type, and confirmation
        let rules = [
            { ok: passwordLength >= 10, label: "Length (min 10)", count: passwordLength },
            { ok: uppercaseCount >= 1, label: "Uppercase (min 1)", count: uppercaseCount },
            { ok: lowercaseCount >= 1, label: "Lowercase (min 1)", count: lowercaseCount },
            { ok: numberCount >= 1, label: "Number (min 1)", count: numberCount },
            { ok: specialCount >= 1, label: "Special (min 1)", count: specialCount },
            { ok: matches, label: "Matches Confirm", count: matches ? 1 : 0 },
        ];

        // Conditional Rule - OTP check when not creating a password
        if (type !== "create") {
            rules.unshift({ ok: isOtpValid, label: "OTP Valid", count: isOtpValid ? 1 : 0 });
        }

        // Rendering - map rules into labeled chips
        return (
            <div className="flex flex-wrap items-center gap-1 md:gap-2">
                {rules.map((rule, i) => (
                    <RuleChip key={i} ok={rule.ok} label={rule.label} count={rule.count} />
                ))}
            </div>
        );
    }
);

export default PasswordRulesChips;
